import { useEffect, useRef, useState } from 'react';
import { Badge, Button, Form, Tab, Tabs } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ExamServices } from '../../../../Services/examServices';
import CardBasic from '../../../../components/Cards/Card.basic';
import ExamUserTable from '../SelectedExam/ExamUserTable';
import InvigilatorExamTable from './InvigilatorExamTable';
import { useLoader } from 'components/Loader/Loader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleDot, faPencil } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import EditExamModal from 'components/Modals/EditExamModal';
import UpdateExamInstructionModal from 'components/UpdateExamInstructionModal';

export default function ExamDetails() {
  const params: any = useParams();
  const { setLoading } = useLoader();

  const [examData, setExamData] = useState<any>({});
  const [userData, setUserData] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageCount, setPageCount] = useState(20);
  const [searchText, setSearchText] = useState<string>('');
  const [filterData, setFilterData] = useState<any>();
  const [activeTab, setActiveTab] = useState<string>('candidates');
  const [showEditModal, setShowEditModal] = useState(false);
  const [invigilatorData, setInvigilatorData] = useState<any[]>([]);

  const [showEditExamInstructionModal, setShowEditExamInstructionModal] = useState(false);

  const getExamById = async () => {
    await ExamServices.getExamById(params.examId)
      .then((res) => {
        if (res.status === 200) {
          setExamData(res.data.data);
        }
      })
      .catch(() => {
        toast.error('Unable to fetch exam details');
      });
  };

  const hasFetchedMapped = useRef(false);
  const getAllMappedUser = async () => {
    setLoading(true);
    await ExamServices.getAllUsers(
      params.examId,
      searchText,
      filterData?.completionStatus,
      filterData?.invigilatorSubmitForReview,
      filterData?.studentSubmitForReview,
      filterData?.isGenerated,
      filterData?.notGenerated,
      filterData?.readyToBeGenerated
    )
      .then((res) => {
        setUserData(res.data.data.users);
        setTotalPages(1);
      })
      .catch(() => {
        toast.error('Unable to fetch User data');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getExamInvigilatorStats = async () => {
    await ExamServices.getExamInvigilatorStats(params.examId)
      .then((res) => {
        if (res.status === 200) {
          setInvigilatorData(res.data.invigilatorsData || []);
        }
      })
      .catch(() => {
        toast.error('Unable to fetch invigilator data');
      });
  };

  const formatDate = (date: any) => {
    return moment(date).format('DD-MMM-YYYY hh:mm a');
  };

  const totalDuration = examData?.sections?.reduce((sum: number, s: any) => sum + (Number(s.duration) || 0), 0) || 0;

  useEffect(() => {
    getExamById();
    getExamInvigilatorStats();
  }, [params.examId]);

  useEffect(() => {
    if (!params.examId) return;
    // Prevent the initial double-fire from examData changing
    if (!hasFetchedMapped.current) {
      hasFetchedMapped.current = true;
      getAllMappedUser();
      return;
    }
    getAllMappedUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, JSON.stringify(filterData)]);

  return (
    <>
      {/* Exam Detail Card */}
      <CardBasic
        cardClass="mt-2"
        body={
          <>
            <div className="d-flex justify-content-between align-items-start mt-2">
              <div>
                <h5 className="mb-1">{examData?.examName || '—'}</h5>
                <div className="text-muted mb-1">Subject: <b>{examData?.subject || '—'}</b></div>

                <div className="d-flex gap-4 flex-wrap mt-2" style={{ fontSize: '13px' }}>
                  <div>
                    Status:{' '}
                    {examData.status === 'live' && (
                      <b className="text-danger">LIVE <FontAwesomeIcon icon={faCircleDot} className="ms-1" /></b>
                    )}
                    {examData.status === 'completed' && (
                      <b className="text-success">COMPLETED <FontAwesomeIcon icon={faCircleDot} className="ms-1" /></b>
                    )}
                    {examData.status === 'upcoming' && (
                      <b className="text-info">UPCOMING <FontAwesomeIcon icon={faCircleDot} className="ms-1" /></b>
                    )}
                  </div>
                  <div>Fees: <b>{examData?.fees ? `₹${examData.fees}` : 'Free'}</b></div>
                  <div>Students/Invigilator: <b>{examData?.studentPerInvigilator ?? 10}</b></div>
                  <div>Min Questions for Review: <b>{examData?.minimumQuestionsRequiredForReview ?? 0}</b></div>
                  <div>Questions for Certificate: <b>{examData?.questionCountForCertificate ?? '—'}</b></div>
                  <div>Total Duration: <b>{totalDuration > 0 ? `${totalDuration} min` : '—'}</b></div>
                </div>

                <div className="d-flex justify-content-start align-items-center mt-2">
                  {examData.tags?.map((tag: any, i: number) => (
                    <Badge bg="secondary" className="me-2" key={i}>{tag}</Badge>
                  ))}
                </div>

                <div className="mt-2 text-muted" style={{ fontSize: '12px' }}>
                  Sections: {examData?.sections?.map((s: any) => `${s.name} (${s.duration || 0} min)`).join(', ') || '—'}
                </div>
              </div>

              <div className="text-end" style={{ minWidth: '200px' }}>
                <div className="text-muted" style={{ fontSize: '12px' }}>
                  Created: <b>{formatDate(examData.createdAt)}</b>
                </div>
                <div className="text-muted" style={{ fontSize: '12px' }}>
                  Updated: <b>{formatDate(examData.updatedAt)}</b>
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end align-items-center mt-3 gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                className="mt-2"
                onClick={() => setShowEditModal(true)}
              >
                <FontAwesomeIcon icon={faPencil} className="me-1" />
                Edit Exam
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                className="mt-2"
                onClick={() => setShowEditExamInstructionModal(true)}
              >
                <FontAwesomeIcon icon={faPencil} className="me-1" />
                Update Exam Instruction
              </Button>
            </div>
          </>
        }
      />

      {/* Tabs */}
      <CardBasic
        cardClass="mt-3"
        title={
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Exam Management</h5>
            {activeTab === 'candidates' && (
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="Search..."
                  style={{ borderRadius: 50 }}
                  onChange={(e: any) => setSearchText(e.target.value)}
                />
              </Form.Group>
            )}
          </div>
        }
        body={
          <Tabs
            activeKey={activeTab}
            onSelect={(k: any) => setActiveTab(k)}
            className="mb-3"
          >
            <Tab eventKey="candidates" title="Candidates">
              <ExamUserTable
                pageCount={pageCount}
                setPageCount={setPageCount}
                userData={userData}
                pageNumber={pageNumber}
                setPageNumber={setPageNumber}
                totalPages={totalPages}
                reloadData={getAllMappedUser}
                filterData={filterData}
                setFilterData={setFilterData}
              />
            </Tab>
            <Tab eventKey="invigilator" title="Invigilator">
              <InvigilatorExamTable
                examId={params.examId}
                invigilatorData={invigilatorData}
                reload={getExamInvigilatorStats}
              />
            </Tab>
          </Tabs>
        }
      />

      {showEditModal && (
        <EditExamModal
          examId={params.examId}
          show={showEditModal}
          setShow={setShowEditModal}
          reloadData={getExamById}
        />
      )}

       {showEditExamInstructionModal && (
        <UpdateExamInstructionModal
          examId={params.examId}
          show={showEditExamInstructionModal}
          setShow={setShowEditExamInstructionModal}
          reloadData={getExamById}
        />
      )}
    </>
  );
}
